import { useMemo } from 'react';
import { ButtonBase, CircularProgress } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconModified } from 'src/assets/icons/Modified';
import { CustomSwitch } from 'src/components/CustomSwitch';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { StaticPanel } from 'src/components/StaticPanel';
import { LANGUAGE } from 'src/data/generation';
import { useMaintainBrandRulesData } from 'src/services';
import { useBrandVoiceIdsForBrandTerms, useBrandVoicesAssets } from 'src/store';

import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

export const BlockBrandVocabulary = ({ className, disabled, language }) => {
  const styles = useStyles();

  const { brandRulesEnabled, brandVoiceIdsForBrandTerms } = useBrandVoiceIdsForBrandTerms();
  const { brandVoicesAssets, brandTerms, loadingBrandVoiceAssets } = useBrandVoicesAssets();
  const { handleSettingsClick, handleActivityToggle, handleDefineBrandRules } = useHandlers({
    brandVoicesAssets,
    language,
  });

  const activeBrandVoices = useMemo(
    () =>
      brandVoicesAssets?.filter((brandVoice) =>
        brandVoiceIdsForBrandTerms.includes(brandVoice?.id)
      ),
    [brandVoiceIdsForBrandTerms, brandVoicesAssets]
  );

  const activeBrandVoiceNames = useMemo(
    () => activeBrandVoices?.map((brandVoice) => brandVoice?.name).join(', ') || '',
    [activeBrandVoices]
  );

  useMaintainBrandRulesData({ language });

  return (
    <StaticPanel
      className={className}
      title="Brand vocabulary"
      titleBold
      data-testid="BlockBrandVocabulary"
    >
      <div className={cx(styles.field, { [styles.disabled]: disabled })}>
        <CustomSwitch
          className={styles.switch}
          checked={brandRulesEnabled}
          onChange={brandTerms.length > 0 ? handleActivityToggle : handleDefineBrandRules}
          color="primary"
          disabled={disabled || loadingBrandVoiceAssets}
        />

        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          Apply brand vocabulary
        </CustomTypography>

        {loadingBrandVoiceAssets ? (
          <CircularProgress size={16} />
        ) : (
          brandTerms.length > 0 && (
            <CustomTooltip title={activeBrandVoiceNames}>
              <ButtonBase
                onClick={handleSettingsClick}
                disabled={disabled}
              >
                {brandRulesEnabled && activeBrandVoices.length > 1 && (
                  <span>{activeBrandVoices.length} vocabularies</span>
                )}
                <IconModified className={styles.icon} />
              </ButtonBase>
            </CustomTooltip>
          )
        )}
      </div>
    </StaticPanel>
  );
};

BlockBrandVocabulary.displayName = 'BlockBrandVocabulary';
BlockBrandVocabulary.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
};
BlockBrandVocabulary.defaultProps = {
  className: null,
  disabled: false,
  language: null,
};
